import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  type ReactNode,
} from 'react';

let publicMotionHydrated = false;
let pendingPublicMotionBootMode: 'csr' | 'ssr' | null = null;

type PublicMotionContextValue = {
  ssrGateActive: boolean;
  suppressMountAnimations: boolean;
};

const PublicMotionContext = createContext<PublicMotionContextValue>({
  ssrGateActive: true,
  suppressMountAnimations: true,
});

function shouldUseSsrMotionGate() {
  if (typeof window === 'undefined') return true;
  if (publicMotionHydrated) return false;
  if (pendingPublicMotionBootMode !== 'ssr') return false;

  pendingPublicMotionBootMode = null;
  return true;
}

export function PublicMotionProvider({ children }: { children: ReactNode }) {
  const [motionContext, setMotionContext] = useState<PublicMotionContextValue>(() => {
    const ssrGateActive = shouldUseSsrMotionGate();

    return {
      ssrGateActive,
      suppressMountAnimations: ssrGateActive,
    };
  });

  useEffect(() => {
    publicMotionHydrated = true;

    if (motionContext.ssrGateActive) {
      // Il contenuto SSR era già visibile: al flip i componenti rimontano come
      // motion veri, ma le animazioni di mount restano soppresse per evitare
      // il flash visibile → hidden → re-ingresso.
      setMotionContext({ ssrGateActive: false, suppressMountAnimations: true });
      return;
    }

    if (motionContext.suppressMountAnimations) {
      // Commit del flip completato: i mount successivi (navigazioni Inertia)
      // tornano ad animare normalmente.
      setMotionContext({ ssrGateActive: false, suppressMountAnimations: false });
    }
  }, [motionContext]);

  return <PublicMotionContext.Provider value={motionContext}>{children}</PublicMotionContext.Provider>;
}

export function usePublicMotionSettings() {
  return useContext(PublicMotionContext);
}

export function resetPublicMotionForTests() {
  publicMotionHydrated = false;
  pendingPublicMotionBootMode = null;
}

export function setPublicMotionBootMode(mode: 'csr' | 'ssr') {
  pendingPublicMotionBootMode = mode;
}

export function clearPendingPublicMotionBootMode() {
  pendingPublicMotionBootMode = null;
}

export function PublicMotionBootTracker({ children }: { children: ReactNode }) {
  useLayoutEffect(() => {
    clearPendingPublicMotionBootMode();
  }, []);

  return children;
}
